import { queryClient } from "./queryClient";
import type { Avatar, ExperimentEntryTarget, UserProfile } from "../types";

const directInterviewStartInFlight = new Map<string, Promise<StartExperimentDirectInterviewPayload>>();
const LAST_FINISHED_INTERVIEW_ID_KEY = "dentalAvatarLastFinishedInterviewId";

export function isExperimentDirectEntryUser(user?: UserProfile | null): boolean {
  return Boolean(
    user?.experimentEntryFlow === "ab_direct_interview" &&
    user?.experimentEntryTarget?.conditionInstructionId
  );
}

function buildDirectInterviewStartKey(target: ExperimentEntryTarget): string {
  return [
    target.flow,
    target.conditionInstructionId,
    target.avatarId ?? "auto",
  ].join(":");
}

export function rememberFinishedInterviewId(interviewId: number | null | undefined): void {
  if (typeof window === "undefined" || typeof interviewId !== "number") return;
  try {
    window.sessionStorage.setItem(LAST_FINISHED_INTERVIEW_ID_KEY, String(interviewId));
  } catch {
    // sessionStorage が使えない環境では server 側 interviewStatus を正とする。
  }
}

export function readRememberedFinishedInterviewId(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LAST_FINISHED_INTERVIEW_ID_KEY);
    const value = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

export function clearRememberedFinishedInterviewId(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LAST_FINISHED_INTERVIEW_ID_KEY);
  } catch {
    // no-op
  }
}

export type StartExperimentDirectInterviewOptions = {
  /** スコア画面などからの再開時は true。既存 interview の再利用を避けて新規面接を開始する */
  restart?: boolean;
};

type StartExperimentDirectInterviewPayload = {
  avatar?: {
    id?: number;
    name?: string;
    age?: number;
    birthDate?: string | null;
    gender?: string;
  };
  videoFiles?: Avatar["videoFiles"];
  interviewId?: number | null;
};

function syncDirectInterviewCache(payload: StartExperimentDirectInterviewPayload): void {
  if (!payload.avatar?.id || !payload.avatar.name || !payload.avatar.gender) return;

  const currentAvatar: Avatar & { interviewId?: number | null } = {
    id: payload.avatar.id,
    name: payload.avatar.name,
    age: payload.avatar.age,
    birthDate: payload.avatar.birthDate ?? null,
    gender: payload.avatar.gender,
    type: "patient",
    videoFiles: payload.videoFiles,
    interviewId: payload.interviewId ?? null,
  };

  queryClient.setQueryData(["/api/avatar/current"], currentAvatar);
  queryClient.removeQueries({ queryKey: ["/api/current-interview/documents"] });
}

export async function startExperimentDirectInterview(
  target: ExperimentEntryTarget,
  options?: StartExperimentDirectInterviewOptions,
): Promise<StartExperimentDirectInterviewPayload> {
  if (!target.conditionInstructionId) {
    throw new Error("直接面接開始に必要な課題文IDがありません");
  }

  const restart = options?.restart === true;
  const key = restart
    ? `${buildDirectInterviewStartKey(target)}:restart`
    : buildDirectInterviewStartKey(target);
  const existing = directInterviewStartInFlight.get(key);
  if (existing) return existing;

  const request = (async () => {
    const response = await fetch("/api/start-interview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        instructionId: target.conditionInstructionId,
        ...(target.avatarId ? { avatarId: target.avatarId } : {}),
        ...(restart ? { restart: true } : {}),
      }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || "面接の準備に失敗しました");
    }

    const payload = await response.json();
    syncDirectInterviewCache(payload);
    return payload;
  })();

  directInterviewStartInFlight.set(key, request);
  request.finally(() => {
    directInterviewStartInFlight.delete(key);
  }).catch(() => {
    // 呼び出し側の await にエラーを返しつつ、finally の unhandled rejection を避ける。
  });

  return request;
}

export async function startFreshExperimentDirectInterview(
  target: ExperimentEntryTarget,
): Promise<StartExperimentDirectInterviewPayload> {
  clearRememberedFinishedInterviewId();
  return startExperimentDirectInterview(target, { restart: true });
}
